import { useMemo, useState, type ReactNode } from 'react';
import { api, ApiError } from '../../lib/api.js';
import { useQuery } from '../../lib/useQuery.js';
import { Page } from '../../app/AppShell.js';
import { Modal } from '../../components/Modal.js';
import { useToast } from '../../components/Toast.js';
import { Loading, ErrorNote } from '../../components/ui.js';
import type { PermissionCatalogue, Role } from '../../lib/types.js';

export function RolesScreen(): ReactNode {
  const roles = useQuery(() => api.roles(), []);
  const catalogue = useQuery(() => api.permissionCatalogue(), []);
  const toast = useToast();
  const [editing, setEditing] = useState<Role | 'new' | null>(null);

  async function remove(role: Role): Promise<void> {
    if (!confirm(`Delete role “${role.label}”?`)) return;
    try {
      await api.deleteRole(role.id);
      roles.refetch();
    } catch (err) {
      toast.error(err);
    }
  }

  return (
    <Page
      title="Roles"
      actions={
        <button className="btn primary" onClick={() => setEditing('new')} disabled={!catalogue.data}>
          New role
        </button>
      }
    >
      {(roles.loading && !roles.data) || (catalogue.loading && !catalogue.data) ? (
        <Loading what="roles" />
      ) : null}
      {roles.error ? <ErrorNote error={roles.error} /> : null}

      {roles.data ? (
        <div className="card">
          <table className="grid">
            <thead>
              <tr>
                <th>Role</th>
                <th>Permissions</th>
                <th>Users</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {roles.data.map((r) => (
                <tr key={r.id}>
                  <td>
                    {r.label} {r.isSystem ? <span className="badge flat">built-in</span> : null}
                  </td>
                  <td className="muted small">
                    {r.permissions.includes('*') ? 'Everything' : `${r.permissions.length} permission(s)`}
                  </td>
                  <td className="muted small">{r.userCount}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {r.isSystem ? (
                      <span className="faint small">—</span>
                    ) : (
                      <div className="row">
                        <button className="btn small" onClick={() => setEditing(r)}>
                          Edit
                        </button>
                        <button className="btn small danger" onClick={() => void remove(r)}>
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {editing && catalogue.data ? (
        <RoleModal
          role={editing === 'new' ? null : editing}
          catalogue={catalogue.data}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            roles.refetch();
          }}
        />
      ) : null}
    </Page>
  );
}

function RoleModal({
  role,
  catalogue,
  onClose,
  onSaved,
}: {
  role: Role | null;
  catalogue: PermissionCatalogue;
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const [label, setLabel] = useState(role?.label ?? '');
  const [name, setName] = useState(role?.name ?? '');
  const [selected, setSelected] = useState<Set<string>>(new Set(role?.permissions ?? []));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const autoName = useMemo(
    () =>
      label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, ''),
    [label],
  );

  function toggle(slug: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }

  async function save(): Promise<void> {
    setBusy(true);
    setError(undefined);
    const body = {
      name: (role?.name ?? name ?? autoName) || autoName,
      label: label.trim(),
      permissions: [...selected],
    };
    try {
      if (role) await api.updateRole(role.id, body);
      else await api.createRole(body);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? (err.fieldMessage() ?? err.message) : String(err));
      setBusy(false);
    }
  }

  return (
    <Modal title={role ? `Edit ${role.label}` : 'New role'} onClose={onClose} wide>
      <div className="stack">
        <label className="field">
          Name
          <input value={label} onChange={(e) => setLabel(e.target.value)} autoFocus />
        </label>
        {!role ? (
          <label className="field">
            Slug
            <span className="hint">Lower-case identifier. Defaults from the name.</span>
            <input value={name || autoName} onChange={(e) => setName(e.target.value)} />
          </label>
        ) : null}

        {catalogue.data.map((group) => (
          <fieldset key={group.group} className="card card-pad" style={{ border: '1px solid var(--border)' }}>
            <legend className="small muted">{group.group}</legend>
            <div className="stack" style={{ gap: 4 }}>
              {group.permissions.map((p) => (
                <label key={p.slug} className="row" style={{ gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={selected.has(p.slug)}
                    onChange={() => toggle(p.slug)}
                  />
                  <span className="small">{p.label}</span>
                  <span className="faint mono" style={{ fontSize: 11 }}>
                    {p.slug}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        ))}

        {error ? <div className="form-error">{error}</div> : null}
        <div className="row between">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy || label.trim() === ''} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save role'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
